import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as RPE,
  type ReactNode,
} from "react";
import {
  formatM2,
  formatMeters,
  polyCenter,
  rectToPoly,
  type FloorDto,
  type ObligationStatus,
  type RoomDto,
} from "@klantpunt/shared";
import { loadPlanPdf, pxScale } from "../../lib/pdf";
import { r4, type DrawState, type Point } from "./drawState";
import { AssetBadge } from "../assets/assetIcons";

export const ZMIN = 0.4;
export const ZMAX = 8;

/** Pin op de tekening (één per plek met coördinaat). */
export interface PinItem {
  key: string;
  issueId: string;
  x: number;
  y: number;
  seq: number;
  priority: string;
  /** Extra plek (gestippeld). */
  extra: boolean;
  /** Afgerond (grijs/transparant). */
  done: boolean;
  selected: boolean;
  label: string;
}

/** Asset op de tekening: pictogram van de elementgroep. */
export interface AssetMarker {
  id: string;
  x: number;
  y: number;
  elementCode: string | null;
  /** Assettype: eigen symbool (docs/SYMBOLEN.md). */
  assetType?: string | null;
  label: string;
  selected: boolean;
  /** Slechtste keuringsstatus; kleurt de rand van de pin. */
  status?: ObligationStatus | null;
  /** Kaartlaag conditie (gemeten of theoretisch): kleurt de rand en toont het cijfer; null = onbekend. */
  condition?: number | null;
  /** Meter (docs/METERSTANDEN.md): meter-pictogram. */
  meter?: boolean;
  /** Hoort bij dezelfde samengestelde asset als de gekozen asset (gestippelde rand). */
  related?: boolean;
}

interface Props {
  floor: FloorDto & { plan: NonNullable<FloorDto["plan"]> };
  pins?: PinItem[];
  /** Assetlaag. */
  assets?: AssetMarker[];
  onAssetClick?: (id: string) => void;
  ghost?: { x: number; y: number } | null;
  /** Tikmenu op een punt van de tekening (op telefoon als bottom sheet). */
  menu?: { x: number; y: number; content: ReactNode } | null;
  /** Tik zonder slepen (niet tijdens intekenen). */
  onTap?: (x: number, y: number) => void;
  onPinClick?: (issueId: string) => void;
  /** Midden van de viewport op dit punt zetten (n wisselt per verzoek). */
  focus?: { x: number; y: number; n: number } | null;
  /** Cursor-hint: tikken plaatst iets. */
  placing?: boolean;
  rooms: RoomDto[];
  zoom: number;
  onZoom: (z: number) => void;
  showRooms: boolean;
  draw: DrawState | null;
  setDraw: (d: DrawState | null) => void;
  /** Vlak afgerond (punten of rechthoek), in genormaliseerde coördinaten. */
  onShape: (points: Point[]) => void;
  onError?: () => void;
  /** Oppervlakte per ruimte (m²) als de tekening een schaal heeft; komt onder de naam. */
  roomAreas?: Map<string, number | null> | null;
  /** Verhouding hoogte/breedte van het blad zodra die bekend is (nodig voor de schaal). */
  onAspect?: (aspect: number) => void;
}

const clamp = (v: number) => Math.min(1, Math.max(0, v));

/**
 * Tekening (pdf.js of afbeelding) met overlay voor ruimtes en intekenen.
 * Breedte = beschikbare breedte × zoom; tijdens zoomen alleen CSS-schaal, daarna scherp opnieuw renderen.
 */
export function PlanStage({
  floor,
  rooms,
  zoom,
  onZoom,
  showRooms,
  draw,
  setDraw,
  onShape,
  onError,
  pins = [],
  assets = [],
  onAssetClick,
  ghost = null,
  menu = null,
  onTap,
  onPinClick,
  focus = null,
  placing = false,
  roomAreas = null,
  onAspect,
}: Props) {
  const stageRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState(0);
  const [aspect, setAspect] = useState<number | null>(
    floor.plan.width && floor.plan.height && floor.planPage === 1 ? floor.plan.height / floor.plan.width : null,
  );
  const [failed, setFailed] = useState(false);
  const [renderedWidth, setRenderedWidth] = useState(0);
  const anchor = useRef<{ ax: number; ay: number; cx: number; cy: number } | null>(null);
  const dragRect = useRef<{ x0: number; y0: number } | null>(null);
  const dragVertex = useRef<number | null>(null);

  const width = Math.max(200, Math.round(avail * zoom));
  const height = aspect ? Math.round(width * aspect) : 0;
  const isPdf = floor.plan.mime === "application/pdf";

  useEffect(() => {
    if (aspect) onAspect?.(aspect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspect]);

  // Beschikbare breedte volgen.
  useLayoutEffect(() => {
    const st = stageRef.current;
    if (!st) return;
    const ro = new ResizeObserver(() => setAvail(Math.max(280, st.clientWidth - 36)));
    ro.observe(st);
    return () => ro.disconnect();
  }, []);

  // Scherp renderen (debounced) na zoom of andere tekening.
  const renderKey = `${floor.plan.id}|${floor.planPage}`;
  useEffect(() => {
    setFailed(false);
  }, [renderKey]);
  useEffect(() => {
    if (!avail) return;
    let cancelled = false;
    let task: { cancel(): void } | null = null;
    const timer = setTimeout(
      async () => {
        try {
          if (isPdf) {
            const doc = await loadPlanPdf(floor.plan.id);
            const page = await doc.getPage(Math.min(floor.planPage, doc.numPages));
            const base = page.getViewport({ scale: 1 });
            const scale = width / base.width;
            const h = base.height * scale;
            const dpr = pxScale(width, h, Math.min(window.devicePixelRatio || 1, 2));
            const vp = page.getViewport({ scale: scale * dpr });
            const c = document.createElement("canvas");
            c.width = Math.floor(vp.width);
            c.height = Math.floor(vp.height);
            c.className = "plannode";
            c.setAttribute("role", "img");
            c.setAttribute("aria-label", `Tekening ${floor.name}`);
            const rt = page.render({ canvas: c, canvasContext: c.getContext("2d")!, viewport: vp });
            task = rt;
            await rt.promise;
            if (cancelled) return;
            setAspect(base.height / base.width);
            hostRef.current?.replaceChildren(c);
          } else {
            const img = new Image();
            img.className = "plannode";
            img.alt = `Tekening ${floor.name}`;
            img.src = `/api/files/${floor.plan.id}/url`;
            await img.decode();
            if (cancelled) return;
            setAspect((img.naturalHeight || 1) / (img.naturalWidth || 1));
            hostRef.current?.replaceChildren(img);
          }
          setRenderedWidth(width);
        } catch (err) {
          if ((err as { name?: string }).name === "RenderingCancelledException" || cancelled) return;
          setFailed(true);
          onError?.();
        }
      },
      renderedWidth ? 240 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
      try {
        task?.cancel();
      } catch {
        /* al klaar */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderKey, width, avail, isPdf]);

  // Scrollpositie behouden rond het zoomanker.
  useLayoutEffect(() => {
    const a = anchor.current;
    const st = stageRef.current;
    const sh = sheetRef.current;
    if (!a || !st || !sh || !height) return;
    anchor.current = null;
    st.scrollLeft = sh.offsetLeft + a.ax * width - a.cx;
    st.scrollTop = sh.offsetTop + a.ay * height - a.cy;
  }, [width, height]);

  /** Zoomen rond een punt in de viewport (cx, cy t.o.v. de stage). */
  const zoomAt = useCallback(
    (z: number, cx?: number, cy?: number, fixed?: { x: number; y: number }) => {
      const st = stageRef.current;
      const sh = sheetRef.current;
      if (!st || !sh || !height) return onZoom(Math.min(ZMAX, Math.max(ZMIN, z)));
      const px = cx ?? st.clientWidth / 2;
      const py = cy ?? st.clientHeight / 2;
      anchor.current = {
        ax: fixed ? fixed.x : (st.scrollLeft + px - sh.offsetLeft) / width,
        ay: fixed ? fixed.y : (st.scrollTop + py - sh.offsetTop) / height,
        cx: px,
        cy: py,
      };
      onZoom(Math.min(ZMAX, Math.max(ZMIN, z)));
    },
    [height, onZoom, width],
  );

  // Muiswiel zoomt rond de cursor; trackpad: twee vingers pannen, knijpen (ctrlKey) zoomt.
  useEffect(() => {
    const st = stageRef.current;
    if (!st) return;
    const onWheel = (e: WheelEvent) => {
      if ((e.target as HTMLElement).closest(".mapmenu")) return;
      e.preventDefault();
      const mouseWheel =
        e.deltaMode === 1 || (e.deltaX === 0 && Math.abs(e.deltaY) >= 40 && Number.isInteger(e.deltaY));
      if (e.ctrlKey || e.metaKey || mouseWheel) {
        const k = e.ctrlKey ? 0.012 : 0.0018;
        const d = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY;
        const r = st.getBoundingClientRect();
        zoomAt(zoom * Math.exp(-d * k), e.clientX - r.left, e.clientY - r.top);
      } else {
        st.scrollLeft += e.deltaX;
        st.scrollTop += e.deltaY;
      }
    };
    st.addEventListener("wheel", onWheel, { passive: false });
    return () => st.removeEventListener("wheel", onWheel);
  }, [zoom, zoomAt]);

  // Slepen pant (muis en één vinger), twee vingers knijpen zoomt. Na slepen geen tik-actie.
  const gest = useRef<{
    pts: Map<number, { x: number; y: number }>;
    pan: { x: number; y: number; sl: number; st: number } | null;
    pinch: { d0: number; z0: number; anchor: { x: number; y: number } } | null;
    moved: boolean;
  }>({ pts: new Map(), pan: null, pinch: null, moved: false });
  const suppressClick = useRef(false);
  const tappedAt = useRef(0);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  function onStagePointerDown(e: RPE) {
    const target = e.target as HTMLElement;
    if (target.closest(".mapmenu, .pin, .vhandle")) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Rechthoek tekenen: slepen tekent, niet pannen.
    if (
      draw?.kind === "draw" &&
      draw.mode === "rect" &&
      !draw.naming &&
      target.closest(".sheet") &&
      gest.current.pts.size === 0
    )
      return;
    if (draw?.kind === "vertices" && dragVertex.current !== null) return;
    const g = gest.current;
    const st = stageRef.current!;
    // Een nieuwe primaire aanraking begint een nieuw gebaar: vergeten pointers (gemiste pointerup) opruimen.
    if (e.isPrimary) {
      g.pts.clear();
      g.pinch = null;
    }
    g.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pts.size === 1) {
      g.pan = { x: e.clientX, y: e.clientY, sl: st.scrollLeft, st: st.scrollTop };
      g.moved = false;
      g.pinch = null;
    } else if (g.pts.size === 2) {
      const [a, b] = [...g.pts.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const r = sheetRef.current?.getBoundingClientRect();
      if (!r) return;
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      g.pinch = {
        d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        z0: zoomRef.current,
        anchor: { x: (mx - r.left) / r.width, y: (my - r.top) / r.height },
      };
      g.moved = true;
      g.pan = null;
    }
  }
  function onStagePointerMove(e: RPE) {
    const g = gest.current;
    if (!g.pts.has(e.pointerId)) return;
    g.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const st = stageRef.current!;
    if (g.pinch && g.pts.size >= 2) {
      const [a, b] = [...g.pts.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const r = st.getBoundingClientRect();
      zoomAt(
        g.pinch.z0 * (Math.hypot(a.x - b.x, a.y - b.y) / g.pinch.d0),
        (a.x + b.x) / 2 - r.left,
        (a.y + b.y) / 2 - r.top,
        g.pinch.anchor,
      );
    } else if (g.pan) {
      const dx = e.clientX - g.pan.x;
      const dy = e.clientY - g.pan.y;
      if (!g.moved && Math.hypot(dx, dy) > 5) {
        g.moved = true;
        st.classList.add("panning");
        try {
          st.setPointerCapture(e.pointerId);
        } catch {
          /* ok */
        }
      }
      if (g.moved) {
        st.scrollLeft = g.pan.sl - dx;
        st.scrollTop = g.pan.st - dy;
      }
    }
  }
  function onStagePointerEnd(e: RPE) {
    const g = gest.current;
    if (!g.pts.has(e.pointerId)) return;
    g.pts.delete(e.pointerId);
    const st = stageRef.current!;
    if (g.pts.size === 1) {
      const [p] = [...g.pts.values()] as [{ x: number; y: number }];
      g.pan = { x: p.x, y: p.y, sl: st.scrollLeft, st: st.scrollTop };
      g.pinch = null;
    }
    if (g.pts.size === 0) {
      if (g.moved) {
        suppressClick.current = true;
        setTimeout(() => (suppressClick.current = false), 60);
      } else if (e.pointerType !== "mouse" && e.type === "pointerup") {
        // Tik zonder slepen: niet afhankelijk van de (soms ontbrekende) click na een gebaar.
        tappedAt.current = Date.now();
        handleTap(e, true);
      }
      g.pan = null;
      g.pinch = null;
      st.classList.remove("panning");
    }
  }

  // Midden van de viewport op een punt zetten ("Toon op tekening").
  useLayoutEffect(() => {
    const st = stageRef.current;
    const sh = sheetRef.current;
    if (!focus || !st || !sh || !height) return;
    st.scrollLeft = sh.offsetLeft + focus.x * width - st.clientWidth / 2;
    st.scrollTop = sh.offsetTop + focus.y * height - st.clientHeight / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n, height > 0]);

  // Externe zoomknoppen sturen via een custom event (houdt het anker in het midden).
  useEffect(() => {
    const st = stageRef.current;
    if (!st) return;
    const onZoomEvent = (e: Event) => zoomAt((e as CustomEvent<number>).detail);
    st.addEventListener("planzoom", onZoomEvent);
    return () => st.removeEventListener("planzoom", onZoomEvent);
  }, [zoomAt]);

  const toNorm = (e: { clientX: number; clientY: number }): Point | null => {
    const r = sheetRef.current?.getBoundingClientRect();
    if (!r || !r.width) return null;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return [r4(x), r4(y)];
  };

  // ---------- intekenen ----------
  const d = draw?.kind === "draw" && !draw.naming ? draw : null;

  function onClick(e: React.MouseEvent) {
    // Touch/pen-tikken zijn al via de pointer-events afgehandeld.
    if (Date.now() - tappedAt.current < 600) return;
    handleTap(e);
  }

  function handleTap(e: { clientX: number; clientY: number; target: EventTarget | null }, fromPointer = false) {
    // De click die de browser na slepen stuurt negeren; tikken uit pointer-events zijn al gefilterd (niet gesleept).
    if (!fromPointer && suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if ((e.target as HTMLElement | null)?.closest(".mapmenu, .pin")) return;
    if (!draw) {
      const p = toNorm(e);
      if (p && onTap) onTap(p[0], p[1]);
      return;
    }
    if (draw.kind === "scale") {
      const p = toNorm(e);
      if (p && draw.pts.length < 2 && draw.lines.length < 2) setDraw({ ...draw, pts: [...draw.pts, p] });
      return;
    }
    if (!d || d.mode !== "poly") return;
    const p = toNorm(e);
    if (!p) return;
    const r = sheetRef.current!.getBoundingClientRect();
    if (d.pts.length >= 3) {
      const [fx, fy] = d.pts[0]!;
      if (Math.hypot((fx - p[0]) * r.width, (fy - p[1]) * r.height) < 16) return onShape(d.pts);
    }
    setDraw({ ...d, pts: [...d.pts, p], hover: null });
  }

  function onPointerDown(e: RPE) {
    if (d?.mode === "rect") {
      const r = sheetRef.current!.getBoundingClientRect();
      dragRect.current = { x0: clamp((e.clientX - r.left) / r.width), y0: clamp((e.clientY - r.top) / r.height) };
      sheetRef.current!.setPointerCapture(e.pointerId);
      e.preventDefault();
    }
  }
  function onPointerMove(e: RPE) {
    const r = sheetRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = clamp((e.clientX - r.left) / r.width);
    const y = clamp((e.clientY - r.top) / r.height);
    if (draw?.kind === "vertices" && dragVertex.current !== null) {
      const points = draw.points.map((p, i): Point => (i === dragVertex.current ? [r4(x), r4(y)] : p));
      setDraw({ ...draw, points });
      return;
    }
    if (d?.mode === "rect" && dragRect.current) {
      const { x0, y0 } = dragRect.current;
      setDraw({ ...d, rect: [r4(Math.min(x, x0)), r4(Math.min(y, y0)), r4(Math.abs(x - x0)), r4(Math.abs(y - y0))] });
    } else if (d?.mode === "poly" && d.pts.length && e.pointerType === "mouse") {
      setDraw({ ...d, hover: [x, y] });
    }
  }
  function onPointerUp() {
    dragVertex.current = null;
    if (!dragRect.current || !d) return;
    dragRect.current = null;
    if (!d.rect) return;
    if (d.rect[2] < 0.01 || d.rect[3] < 0.01) {
      setDraw({ ...d, rect: null });
      return;
    }
    onShape(rectToPoly(d.rect).map(([x, y]) => [r4(x), r4(y)]));
  }

  // ---------- overlay ----------
  const editingId = draw && "roomId" in draw ? draw.roomId : null;
  const shown = rooms.filter((r) => r.shape && r.id !== editingId && (showRooms || draw));
  const pts = (p: readonly (readonly [number, number])[]) => p.map((q) => q.join(",")).join(" ");
  const cur: Point[] | null =
    draw?.kind === "vertices"
      ? draw.points
      : draw?.kind === "draw"
        ? draw.naming
          ? draw.shape
          : draw.mode === "rect" && draw.rect
            ? rectToPoly(draw.rect)
            : draw.pts.length >= 3
              ? draw.pts
              : null
        : null;

  // Tikmenu altijd volledig in beeld (ook ingezoomd en vlak bij de rand): rechts van de pin, of
  // links als het daar niet past; verticaal binnen het tekeningvak. Op telefoon is het een sheet.
  const menuRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = menuRef.current;
    const stage = stageRef.current;
    if (!el || !stage) return;
    el.style.transform = "";
    if (window.matchMedia("(max-width: 760px)").matches) return;
    const box = stage.getBoundingClientRect();
    const r = el.getBoundingClientRect(); // standaard: translate(14px, -50%)
    const ax = r.left - 14;
    const ay = r.top + r.height / 2;
    const pad = 8;
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, Math.max(lo, hi)));
    let x = ax + 14;
    if (x + r.width > box.right - pad) x = ax - 14 - r.width;
    x = clamp(x, box.left + pad, box.right - pad - r.width);
    const y = clamp(ay - r.height / 2, box.top + pad, box.bottom - pad - r.height);
    el.style.transform = `translate(${Math.round(x - ax)}px, ${Math.round(y - ay)}px)`;
  });

  if (failed) {
    return (
      <div className="stage" ref={stageRef}>
        <div className="planerr">
          De tekening kon niet worden geladen. Upload het bestand opnieuw via “Tekening vervangen”.
        </div>
      </div>
    );
  }

  return (
    <div
      // Op 100% of kleiner: verticaal vegen scrolt (touch-action pan-y); ingezoomd pant de tekening.
      className={`stage ${zoom <= 1 ? "fit" : ""}`}
      ref={stageRef}
      data-testid="stage"
      onPointerDown={onStagePointerDown}
      onPointerMove={onStagePointerMove}
      onPointerUp={onStagePointerEnd}
      onPointerCancel={onStagePointerEnd}
    >
      <div
        ref={sheetRef}
        className={`sheet ${draw ? `drawing ${draw.kind === "draw" ? draw.mode : draw.kind}` : ""} ${placing ? "placing" : ""}`}
        style={{ width, height: height || undefined }}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="sheet"
      >
        {/* Wordt imperatief gevuld (canvas/img); geen React-kinderen hierin. */}
        <div ref={hostRef} className="planhost" style={{ width, height: height || undefined }} />
        {!renderedWidth && <div className="planloading">Tekening laden…</div>}
        {height > 0 && (
          <div className="overlay">
            <svg className="roomsvg" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
              {shown.map((r) => (
                <polygon key={r.id} points={pts(r.shape!.points)} />
              ))}
              {cur && <polygon className="cur" points={pts(cur)} />}
              {d?.mode === "poly" && d.pts.length > 0 && <polyline points={pts(d.pts)} />}
              {d?.mode === "poly" && d.hover && d.pts.length > 0 && (
                <line
                  x1={d.pts[d.pts.length - 1]![0]}
                  y1={d.pts[d.pts.length - 1]![1]}
                  x2={d.hover[0]}
                  y2={d.hover[1]}
                />
              )}
            </svg>
            {shown.map((r) => {
              const [cx, cy] = polyCenter(r.shape!.points);
              const m2 = roomAreas?.get(r.id);
              return (
                <span key={r.id} className="rlabel" style={{ left: `${cx * 100}%`, top: `${cy * 100}%` }}>
                  {r.name}
                  {m2 != null && <span className="rarea">{formatM2(m2)}</span>}
                </span>
              );
            })}
            {draw?.kind === "scale" && (
              <>
                <svg className="scalesvg" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
                  {draw.lines.map((l, k) => (
                    <line key={k} x1={l.a[0]} y1={l.a[1]} x2={l.b[0]} y2={l.b[1]} />
                  ))}
                  {draw.pts.length === 2 && (
                    <line
                      className="cur"
                      x1={draw.pts[0]![0]}
                      y1={draw.pts[0]![1]}
                      x2={draw.pts[1]![0]}
                      y2={draw.pts[1]![1]}
                    />
                  )}
                </svg>
                {[...draw.lines.flatMap((l) => [l.a, l.b]), ...draw.pts].map((q, k) => (
                  <span key={k} className="sdot" style={{ left: `${q[0] * 100}%`, top: `${q[1] * 100}%` }} />
                ))}
                {draw.lines.map((l, k) => (
                  <span
                    key={k}
                    className="slabel"
                    style={{ left: `${((l.a[0] + l.b[0]) / 2) * 100}%`, top: `${((l.a[1] + l.b[1]) / 2) * 100}%` }}
                  >
                    {formatMeters(l.meters)}
                  </span>
                ))}
              </>
            )}
            {d?.mode === "poly" &&
              d.pts.map((q, k) => (
                <span
                  key={k}
                  className={`vdot ${k === 0 ? "first" : ""} ${k === 0 && d.pts.length >= 3 ? "ready" : ""}`}
                  style={{ left: `${q[0] * 100}%`, top: `${q[1] * 100}%` }}
                />
              ))}
            {assets.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`pin assetpin${a.selected ? " sel" : ""}${a.related ? " rel" : ""}${
                  a.condition !== undefined ? ` cond-${a.condition ?? "none"}` : a.status ? ` ob-${a.status}` : ""
                }`}
                style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%` }}
                aria-label={a.label}
                title={a.label}
                data-asset={a.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onAssetClick?.(a.id);
                }}
              >
                <AssetBadge code={a.elementCode} size={28} meter={a.meter} type={a.assetType} />
                {a.condition != null && (
                  <span className="condnum" aria-hidden="true">
                    {a.condition}
                  </span>
                )}
              </button>
            ))}
            {pins.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`pin prio-${p.priority} ${p.selected ? "sel" : ""} ${p.done ? "done" : ""}`}
                style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                aria-label={p.label}
                title={p.label}
                data-issue={p.issueId}
                onClick={(e) => {
                  e.stopPropagation();
                  onPinClick?.(p.issueId);
                }}
              >
                <svg viewBox="0 0 30 38" aria-hidden="true">
                  <path
                    d="M15 37C15 37 2 23.5 2 14.5A13 13 0 0 1 28 14.5C28 23.5 15 37 15 37Z"
                    strokeDasharray={p.extra ? "3 2" : undefined}
                  />
                </svg>
                <span className="lbl">{p.seq || "•"}</span>
              </button>
            ))}
            {ghost && (
              <span
                className="pin ghost"
                style={{ left: `${ghost.x * 100}%`, top: `${ghost.y * 100}%` }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 30 38">
                  <path d="M15 37C15 37 2 23.5 2 14.5A13 13 0 0 1 28 14.5C28 23.5 15 37 15 37Z" />
                </svg>
                <span className="lbl">+</span>
              </span>
            )}
            {menu && (
              <div
                ref={menuRef}
                className="mapmenu"
                style={{ left: `${menu.x * 100}%`, top: `${menu.y * 100}%` }}
                role="menu"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {menu.content}
              </div>
            )}
            {draw?.kind === "vertices" &&
              draw.points.map((q, k) => (
                <button
                  key={k}
                  type="button"
                  className={`vhandle ${draw.active === k ? "active" : ""}`}
                  style={{ left: `${q[0] * 100}%`, top: `${q[1] * 100}%` }}
                  aria-label={`Hoekpunt ${k + 1} van ${draw.points.length}. Pijltjestoetsen verplaatsen.`}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    dragVertex.current = k;
                    sheetRef.current?.setPointerCapture(e.pointerId);
                    setDraw({ ...draw, active: k });
                  }}
                  onFocus={() => draw.active !== k && setDraw({ ...draw, active: k })}
                  onKeyDown={(e) => {
                    const step = e.shiftKey ? 0.01 : 0.002;
                    const delta: Record<string, Point> = {
                      ArrowLeft: [-step, 0],
                      ArrowRight: [step, 0],
                      ArrowUp: [0, -step],
                      ArrowDown: [0, step],
                    };
                    const dv = delta[e.key];
                    if (!dv) return;
                    e.preventDefault();
                    const points = draw.points.map((p, i): Point =>
                      i === k ? [r4(clamp(p[0] + dv[0])), r4(clamp(p[1] + dv[1]))] : p,
                    );
                    setDraw({ ...draw, points, active: k });
                  }}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Zoom van buitenaf (knoppen) zodat het midden van de viewport blijft staan. */
export function requestZoom(z: number) {
  document.querySelector(".stage")?.dispatchEvent(new CustomEvent("planzoom", { detail: z }));
}
